import axios from 'axios';
import { API_BASE_URL, getHealthUrl } from '@/config/env';
import { getUploadTimeoutMs } from '@/utils/uploadTimeout';
import { axiosClient } from './axiosClient';
import { ENDPOINTS } from './endpoints';
import { getErrorMessage } from './errors';

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isRetryableUploadError(error) {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  if (status === 400 || status === 401 || status === 403 || status === 413) return false;
  if (status === 408 || status === 429 || (status != null && status >= 500)) return true;
  if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') return true;
  return !error.response;
}

function unwrapData(raw) {
  if (raw && typeof raw === 'object' && 'data' in raw) {
    return raw.data;
  }
  return raw;
}

function toQueryString(params) {
  if (!params) return '';
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.set(key, String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

const HEALTH_CACHE_MS = 60_000;
let healthCache = null;
let healthCacheAt = 0;
let healthInFlight = null;

async function fetchHealthFromApi() {
  const { data } = await axios.get(getHealthUrl());
  return data;
}

function getCachedHealth() {
  if (!healthCache) return null;
  if (Date.now() - healthCacheAt > HEALTH_CACHE_MS) return null;
  return healthCache;
}

async function requestHealth({ force = false } = {}) {
  if (!force) {
    const cached = getCachedHealth();
    if (cached) return cached;
  }

  if (!healthInFlight) {
    healthInFlight = fetchHealthFromApi()
      .then((data) => {
        healthCache = data;
        healthCacheAt = Date.now();
        return data;
      })
      .finally(() => {
        healthInFlight = null;
      });
  }

  return healthInFlight;
}

export const dataService = {
  async get(endpoint, config) {
    const { data } = await axiosClient.get(endpoint, config);
    return unwrapData(data);
  },

  async post(endpoint, payload, config) {
    const { data } = await axiosClient.post(endpoint, payload, config);
    return unwrapData(data);
  },

  async put(endpoint, payload, config) {
    const { data } = await axiosClient.put(endpoint, payload, config);
    return unwrapData(data);
  },

  async patch(endpoint, payload, config) {
    const { data } = await axiosClient.patch(endpoint, payload, config);
    return unwrapData(data);
  },

  async delete(endpoint, config) {
    await axiosClient.delete(endpoint, config);
  },

  async list(endpoint, params) {
    const suffix = toQueryString(params);
    const { data } = await axiosClient.get(`${endpoint}${suffix}`);
    const unwrapped = unwrapData(data);
    return Array.isArray(unwrapped) ? unwrapped : [];
  },

  async getById(endpointBuilder, id) {
    const { data } = await axiosClient.get(endpointBuilder(id));
    return unwrapData(data);
  },

  async create(endpoint, payload) {
    const { data } = await axiosClient.post(endpoint, payload);
    return unwrapData(data);
  },

  async update(endpointBuilder, id, payload) {
    const { data } = await axiosClient.patch(endpointBuilder(id), payload);
    return unwrapData(data);
  },

  async removeById(endpointBuilder, id) {
    await axiosClient.delete(endpointBuilder(id));
  },

  async deleteUser(userId) {
    await axiosClient.delete(ENDPOINTS.users.byId(userId));
  },

  async renameTagCategory(oldCategory, newCategory) {
    const { data } = await axiosClient.put(ENDPOINTS.tags.renameCategory, {
      oldCategory,
      newCategory,
    });
    return unwrapData(data);
  },

  // --- Domain: health & migrate ---
  async checkHealth(options = {}) {
    return requestHealth(options);
  },

  async migrateLocalStorage(payload) {
    return this.post(ENDPOINTS.migrate.base, payload);
  },

  // --- Domain: users ---
  async getUser(userId) {
    return this.getById(ENDPOINTS.users.byId, userId);
  },

  async updateUser(userId, payload) {
    return this.update(ENDPOINTS.users.byId, userId, payload);
  },

  async createUser(payload) {
    return this.create(ENDPOINTS.users.base, payload);
  },

  async resetUserPassword(userId, payload) {
    return this.post(ENDPOINTS.users.resetPassword(userId), payload);
  },

  // --- Domain: responses ---
  async listResponses(params) {
    return this.list(ENDPOINTS.responses.base, params);
  },

  async getResponse(responseId) {
    return this.getById(ENDPOINTS.responses.byId, responseId);
  },

  async createResponse(payload) {
    return this.create(ENDPOINTS.responses.base, payload);
  },

  async updateResponse(responseId, payload) {
    return this.update(ENDPOINTS.responses.byId, responseId, payload);
  },

  // --- Domain: audit ---
  async listAuditLogs(params) {
    return this.list(ENDPOINTS.auditLogs.base, params);
  },

  // --- Domain: elements ---
  async getElement(elementId) {
    return this.getById(ENDPOINTS.elements.byId, elementId);
  },

  async updateElement(elementId, payload) {
    return this.update(ENDPOINTS.elements.byId, elementId, payload);
  },

  // --- Domain: exercises ---
  async getExercise(exerciseId) {
    return this.getById(ENDPOINTS.exercises.byId, exerciseId);
  },

  async getExerciseQuestionMedia(exerciseId, questionId) {
    return this.get(
      `${ENDPOINTS.exercises.questionMedia(exerciseId)}${toQueryString({ questionId })}`,
      { skipGlobalLoader: true }
    );
  },

  async getExerciseDraft(exerciseId, params) {
    return this.get(
      `${ENDPOINTS.exercises.draft(exerciseId)}${toQueryString(params)}`,
      { skipGlobalLoader: true }
    );
  },

  async saveExerciseDraft(exerciseId, payload) {
    return this.put(
      ENDPOINTS.exercises.draft(exerciseId),
      payload,
      { skipGlobalLoader: true }
    );
  },

  async deleteExerciseDraft(exerciseId, params) {
    return this.delete(
      `${ENDPOINTS.exercises.draft(exerciseId)}${toQueryString(params)}`,
      { skipGlobalLoader: true }
    );
  },

  async getMyExerciseDrafts(params) {
    return this.get(
      `${ENDPOINTS.exercises.myDrafts}${toQueryString(params)}`,
      { skipGlobalLoader: true }
    );
  },

  // --- Domain: link tables ---
  async listBatchBooks(params) {
    return this.list(ENDPOINTS.batchBooks.base, params);
  },

  async createBatchBook(payload) {
    return this.create(ENDPOINTS.batchBooks.base, payload);
  },

  async deleteBatchBook(linkId) {
    return this.removeById(ENDPOINTS.batchBooks.byId, linkId);
  },

  async listBookLessons(params) {
    return this.list(ENDPOINTS.bookLessons.base, params);
  },

  async createBookLesson(payload) {
    return this.create(ENDPOINTS.bookLessons.base, payload);
  },

  async deleteBookLesson(linkId) {
    return this.removeById(ENDPOINTS.bookLessons.byId, linkId);
  },

  async listLessonElements(params) {
    return this.list(ENDPOINTS.lessonElements.base, params);
  },

  async listLessonElementsPage(params) {
    return this.get(`${ENDPOINTS.lessonElements.base}${toQueryString(params)}`);
  },

  async createLessonElement(payload) {
    return this.create(ENDPOINTS.lessonElements.base, payload);
  },

  async updateLessonElement(linkId, payload) {
    return this.update(ENDPOINTS.lessonElements.byId, linkId, payload);
  },

  async deleteLessonElement(linkId) {
    return this.removeById(ENDPOINTS.lessonElements.byId, linkId);
  },

  async listLessonExercises(params) {
    return this.list(ENDPOINTS.lessonExercises.base, params);
  },

  async listLessonExercisesPage(params) {
    return this.get(`${ENDPOINTS.lessonExercises.base}${toQueryString(params)}`);
  },

  async getBatchLessonContent({ batchId } = {}) {
    return this.get(`${ENDPOINTS.me.batchContent}${toQueryString({ batchId })}`, {
      skipGlobalLoader: true,
    });
  },

  async getBookLessonContent({ batchId, bookId, type, offset, limit }) {
    return this.get(
      `${ENDPOINTS.me.bookContent}${toQueryString({ batchId, bookId, type, offset, limit })}`,
      {
        skipGlobalLoader: true,
      }
    );
  },

  async getStudentDashboard({ batchId } = {}) {
    return this.get(`${ENDPOINTS.me.studentDashboard}${toQueryString({ batchId })}`, {
      skipGlobalLoader: true,
    });
  },

  async getParentDashboard(params) {
    return this.get(`${ENDPOINTS.me.parentDashboard}${toQueryString(params)}`, {
      skipGlobalLoader: true,
    });
  },

  async getCreatorSummary() {
    return this.get(ENDPOINTS.me.creatorSummary, { skipGlobalLoader: true });
  },

  async createLessonExercise(payload) {
    return this.create(ENDPOINTS.lessonExercises.base, payload);
  },

  async updateLessonExercise(linkId, payload) {
    return this.update(ENDPOINTS.lessonExercises.byId, linkId, payload);
  },

  async deleteLessonExercise(linkId) {
    return this.removeById(ENDPOINTS.lessonExercises.byId, linkId);
  },

  async listNotifications(params) {
    const { data } = await axiosClient.get(
      `${ENDPOINTS.notifications.base}${toQueryString(params)}`,
      { skipGlobalLoader: true }
    );
    const unwrapped = unwrapData(data);
    return Array.isArray(unwrapped) ? unwrapped : [];
  },

  async createNotification(payload) {
    return this.create(ENDPOINTS.notifications.base, payload);
  },

  async updateNotification(id, payload) {
    return this.update(ENDPOINTS.notifications.byId, id, payload);
  },

  async deleteNotification(id) {
    return this.removeById(ENDPOINTS.notifications.byId, id);
  },

  async listQuestionBank(params) {
    return this.list(ENDPOINTS.questionBank.base, params);
  },

  async getQuestionBankItem(id) {
    return this.getById(ENDPOINTS.questionBank.byId, id);
  },

  async createQuestionBankItem(payload) {
    return this.create(ENDPOINTS.questionBank.base, payload);
  },

  async updateQuestionBankItem(id, payload) {
    return this.update(ENDPOINTS.questionBank.byId, id, payload);
  },

  async deleteQuestionBankItem(id) {
    return this.removeById(ENDPOINTS.questionBank.byId, id);
  },

  async listExerciseBanks(params) {
    return this.list(ENDPOINTS.exerciseBank.base, params);
  },

  async getExerciseBank(id) {
    return this.getById(ENDPOINTS.exerciseBank.byId, id);
  },

  async getExerciseBankItem(bankId, itemId) {
    return this.get(ENDPOINTS.exerciseBank.itemById(bankId, itemId));
  },

  async createExerciseBank(payload) {
    return this.create(ENDPOINTS.exerciseBank.base, payload);
  },

  async updateExerciseBank(id, payload) {
    return this.update(ENDPOINTS.exerciseBank.byId, id, payload);
  },

  async deleteExerciseBank(id) {
    return this.removeById(ENDPOINTS.exerciseBank.byId, id);
  },

  async forkExerciseBank(id, payload) {
    return this.post(ENDPOINTS.exerciseBank.fork(id), payload);
  },

  async importExerciseBankItems(bankId, payload) {
    return this.post(ENDPOINTS.exerciseBank.importItems(bankId), payload);
  },

  async deployExerciseBank(bankId, payload) {
    return this.post(ENDPOINTS.exerciseBank.deploy(bankId), payload);
  },

  async createExerciseBankItem(bankId, payload) {
    return this.post(ENDPOINTS.exerciseBank.items(bankId), payload);
  },

  async updateExerciseBankItem(bankId, itemId, payload) {
    return this.patch(ENDPOINTS.exerciseBank.itemById(bankId, itemId), payload);
  },

  async deleteExerciseBankItem(bankId, itemId) {
    return this.delete(ENDPOINTS.exerciseBank.itemById(bankId, itemId));
  },

  async reorderExerciseBankItems(bankId, itemIds) {
    return this.patch(ENDPOINTS.exerciseBank.reorderItems(bankId), { itemIds });
  },

  async createAnalyticsEvent(payload) {
    return this.create(ENDPOINTS.analytics.base, payload);
  },

  async getProgress(params) {
    const suffix = toQueryString(params);
    return this.get(`${ENDPOINTS.progress.base}${suffix}`);
  },

  async listBookCompletions(params) {
    return this.list(ENDPOINTS.bookCompletions.base, params);
  },

  async recordBookCompletion(payload) {
    return this.post(ENDPOINTS.bookCompletions.record, payload);
  },

  async checkBookCompletion(params) {
    const suffix = toQueryString(params);
    return this.get(`${ENDPOINTS.bookCompletions.check}${suffix}`);
  },

  // --- Domain: uploads ---
  async uploadFile(file, options = {}) {
    if (!API_BASE_URL) {
      throw new Error('REACT_APP_API_BASE_URL is not set; cannot upload file.');
    }

    const { onProgress, onRetry, maxAttempts = 3 } = options;
    const timeout = getUploadTimeoutMs(file.size);
    const form = new FormData();
    form.append('file', file);

    let lastError;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        const { data: raw } = await axiosClient.post(ENDPOINTS.uploads.base, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout,
          onUploadProgress: (event) => {
            if (!onProgress || !event.total) return;
            onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
          },
        });
        const data = unwrapData(raw);
        const url = data?.file || data?.url;
        if (!url || typeof url !== 'string') {
          throw new Error('Upload succeeded but no file URL was returned.');
        }
        onProgress?.(100);
        return {
          url,
          fileName: data?.fileName || file.name,
          fileSize: data?.fileSize ?? file.size,
          fileType: data?.fileType ?? file.type,
        };
      } catch (error) {
        lastError = error;
        if (axios.isAxiosError(error) && error.response?.status === 413) {
          throw new Error(
            'File is too large for the server upload limit (413). Please try a smaller file or use a file URL.'
          );
        }
        if (!isRetryableUploadError(error) || attempt >= maxAttempts) {
          throw new Error(getErrorMessage(error, 'Upload failed'));
        }
        onRetry?.(attempt, maxAttempts);
        await sleep(1000 * 2 ** (attempt - 1));
      }
    }

    throw new Error(getErrorMessage(lastError, 'Upload failed'));
  },

  // --- Domain: Zoho Meeting recordings (super-admin automation) ---
  async listZohoRecordings() {
    return this.get(ENDPOINTS.zohoRecordings.base);
  },

  async listZohoVideoQualityOptions() {
    return this.get(ENDPOINTS.zohoRecordings.qualityOptions);
  },

  /** Starts background import; returns { job } immediately. */
  async importZohoRecording(payload) {
    return this.post(ENDPOINTS.zohoRecordings.import, payload);
  },

  async getZohoImportJob(jobId) {
    return this.get(ENDPOINTS.zohoRecordings.importJob(jobId), { skipGlobalLoader: true });
  },

  /** Cancels a queued/running import; server removes partial files. Returns { job }. */
  async cancelZohoImportJob(jobId) {
    return this.post(ENDPOINTS.zohoRecordings.cancelImportJob(jobId), {});
  },

  async listZohoImportJobs() {
    return this.get(ENDPOINTS.zohoRecordings.importJobs, { skipGlobalLoader: true });
  },

  async getZohoAutoImportSettings() {
    return this.get(ENDPOINTS.zohoRecordings.autoImportSettings);
  },

  async updateZohoAutoImportSettings(payload) {
    return this.put(ENDPOINTS.zohoRecordings.autoImportSettings, payload);
  },

  // --- Domain: Fees (UPI) ---
  async getFeeSettings() {
    return this.get(ENDPOINTS.fees.settings);
  },

  async updateFeeSettings(payload) {
    return this.put(ENDPOINTS.fees.settings, payload);
  },

  /** { userIds?, batchId?, title, amount?, dueDate?, note? } → { created, skipped, settingsReady } */
  async markFeesPending(payload) {
    return this.post(ENDPOINTS.fees.markPending, payload);
  },

  /** { feeIds? | userIds? | batchId?, method, note?, title?, amount? } → { updated, created, skipped } */
  async markFeesPaid(payload) {
    return this.post(ENDPOINTS.fees.markPaid, payload);
  },

  async rejectFeeSubmission(feeId, reason) {
    return this.post(ENDPOINTS.fees.reject(feeId), { reason });
  },

  async deleteFee(feeId) {
    return this.delete(ENDPOINTS.fees.byId(feeId));
  },

  /** { status?, batchId?, studentId?, search?, page?, limit? } → { items, total, page, limit } */
  async listFees(params, config) {
    return this.get(`${ENDPOINTS.fees.base}${toQueryString(params)}`, config);
  },

  async getFeeSummary(config) {
    return this.get(ENDPOINTS.fees.summary, config);
  },

  async exportFeesCsv(params) {
    const { data } = await axiosClient.get(`${ENDPOINTS.fees.export}${toQueryString(params)}`, {
      responseType: 'text',
      skipDedupe: true,
    });
    return data;
  },

  /** Student/parent: { settings: { enabled, upiId, payeeName }, fees } */
  async getMyFees(config) {
    return this.get(ENDPOINTS.fees.my, { skipDedupe: true, ...config });
  },

  async submitFeePayment(feeId, { txnId, screenshotDataUrl }) {
    return this.post(ENDPOINTS.fees.submit(feeId), { txnId, screenshotDataUrl });
  },
};
